import { useContext, useEffect, useMemo } from "react";
import { useFlexLayout } from "react-table";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Box,
  DashPlaceholder,
  DropDown,
  Flex,
  Icon,
  Image,
  ModalContext,
  PaginationContext,
  SearchContext,
  SortContext,
  SortHeader,
  Text,
} from "dash-ui";
import { AccountStatusesEnum } from "dash-common";

import DeliveryImage from "assets/images/png/Delivery.png";
import { UserFragment, useGetCmsUsersQuery } from "gql/generated.graphql";

import { StyledTable } from "./ManagementPageTable.styles";

const ManagementPageTable = () => {
  const theme = useTheme();
  const { t } = useTranslation(["common", "tables"]);
  const { state, getRefetchProps, setTotal } = useContext(PaginationContext);
  const { state: sortState } = useContext(SortContext);
  const { searchSlug } = useContext(SearchContext);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const pagination = getRefetchProps(state);

  const { data, loading } = useGetCmsUsersQuery({
    variables: {
      pagination,
      sort: [sortState],
      filter: {
        ...(searchSlug && { searchText: searchSlug }),
      },
    },
  });

  const { users, total } = useMemo(
    () => ({
      users: data?.getCMSUsers.collection || [],
      total: data?.getCMSUsers.totalCount || 0,
    }),
    [data]
  );

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <SortHeader accessor="profile.firstName" isDefault>
            {t("common:labels.fullName")}
          </SortHeader>
        ),
        accessor: "profile.firstName",
        Cell: ({ row }: { row: { original: UserFragment } }) => {
          const { firstName, lastName, avatarFile } = row.original.profile;

          return (
            <Flex alignItems="center">
              <Box mr={4}>
                {avatarFile?.publicUrl ? (
                  <Image src={avatarFile.publicUrl} size={32} />
                ) : (
                  <DashPlaceholder size={32} />
                )}
              </Box>
              <Text>{lastName ? `${firstName} ${lastName}` : firstName}</Text>
            </Flex>
          );
        },
      },
      {
        Header: () => (
          <SortHeader accessor="account.email">
            {t("forms:labels.email")}
          </SortHeader>
        ),
        accessor: "account.email",

        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: () => (
          <Text whiteSpace="nowrap">{t("common:labels.actions")}</Text>
        ),
        accessor: "account._id",
        width: 30,
        Cell: ({ row }: { row: { original: UserFragment } }) => {
          const { _id: userId, status } = row.original.account;
          const isArchived = status === AccountStatusesEnum.ARCHIVED;

          return (
            <Flex alignItems="center" justifyContent="center" h="100%">
              <DropDown
                data-testid="user-actions"
                placementHorizontal="left"
                Anchor={() => <Icon name="dots" />}
                items={[
                  {
                    key: "archive",
                    icon: "archive",
                    children: isArchived
                      ? t("tables:cmsUsers:unarchive")
                      : t("tables:cmsUsers:archive"),
                    w: 170,
                    onClick: () => {
                      setPayload({ userId, archive: !isArchived });
                      toggleModal(true, "ArchiveUserModal");
                    },
                  },
                  {
                    key: "edit",
                    icon: "edit",
                    children: t("tables:cmsUsers:edit"),
                    w: 170,
                    onClick: () => {
                      setPayload({ user: row.original });
                      toggleModal(true, "AddDashStaffUser");
                    },
                  },
                  {
                    key: "delete",
                    icon: "close",
                    children: t("tables:cmsUsers:delete"),
                    w: 170,
                    onClick: () => {
                      setPayload({
                        accountId: row.original.account._id,
                        profileId: row.original.profile._id,
                        userName:
                          row.original.profile.firstName +
                          " " +
                          row.original.profile.lastName,
                      });
                      toggleModal(true, "UserCmsDeleteModal");
                    },
                  },
                ]}
              />
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, t]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const labelToShow = !loading
    ? t("pages:managementPage.emptyPlaceholder") || ""
    : "";

  return (
    <StyledTable
      tableProps={{
        data: users,
        columns,
        loading,
        maxHeight: "calc(100vh - 350px)",
        stickyHeader: true,
        layouts: [useFlexLayout],
        placeholderProps: {
          image: loading ? undefined : DeliveryImage,
          label: labelToShow,
        },
      }}
      paginationProps={{
        label: t("tables:organizationUsers.paginationLabel"),
        nextButtonLabel: t("common:labels.next"),
        previousButtonLabel: t("common:labels.previous"),
      }}
    />
  );
};

export default ManagementPageTable;
